// src/prf/prfOptimizer.js
// The production PRF optimizer. TWO STAGES, and there is no composite score.
//
// ═══════════════════════════════════════════════════════════════════
// STAGE 1 — RISK RELEVANCE
//   Which index intervals plausibly insure the underlying forage-production
//   risk. The METHODOLOGY IS A VALUE and it is stored with every
//   recommendation, so a prior can never be mistaken for a measurement:
//
//     REGIONAL_AGRONOMIC_PRIOR   what ships today. A regional agronomic
//                                judgement about when rainfall matters to WNY
//                                cool-season forage. NOT measured farm behaviour.
//     MEASURED_FORAGE_RESPONSE   declared, NOT implemented. Reserved so a future
//                                measured relationship replaces the prior without
//                                any stored recommendation becoming ambiguous.
//
//   An interval is NOT admitted because it improves payout stability or
//   diversification. Insuring JAN-FEB because it is uncorrelated with JUL-AUG
//   optimises the insurance, not the farm.
//
// STAGE 2 — CONTRACT OPTIMIZATION
//   Inside the Stage-1 eligible universe only, over RMA-VERIFIED legal
//   strategies only. Produces a Pareto frontier on economics vs stability and
//   three named candidates. No weights are applied at any point.
// ═══════════════════════════════════════════════════════════════════
//
// ⚠ FAIL CLOSED. Without RMA-verified percent-of-value limits this module
// enumerates nothing and returns RMA_LIMITS_UNVERIFIED. It never defaults a cap:
// 70% in Genesee NY and 50% in Tom Green TX are both real, so a guessed cap
// produces strategies that are illegal in one county and needlessly narrow in
// another, and calls both "valid".

const { paymentFactor, PRF_INTERVALS } = require("./prfIndexMethodology.js");

const STAGE1_METHODOLOGY = {
  REGIONAL_AGRONOMIC_PRIOR: {
    id: "REGIONAL_AGRONOMIC_PRIOR",
    version: "wny-cool-season-v1",
    label: "Regional agronomic prior — Western NY cool-season forage",
    measured: false,
    basis: "Regional judgement about when rainfall plausibly matters to cool-season forage growth. NOT a measured relationship to any farm's production.",
    intervals: ["APRMAY", "MAYJUN", "JUNJUL", "JULAUG", "AUGSEP"],
  },
  // Declared so stored recommendations can be told apart the day this exists.
  // Nothing produces it yet; see docs/prf-risk-relevance-plan.md.
  MEASURED_FORAGE_RESPONSE: {
    id: "MEASURED_FORAGE_RESPONSE",
    version: null,
    label: "Measured forage response (not implemented)",
    measured: true,
    basis: "A measured relationship between interval index and forage production outcome on this grid.",
    intervals: null,
  },
};

// Alternative season definitions, for the sensitivity analysis. Each is a
// variant of the PRIOR and is labelled as one — none of them is measured.
const PRIOR_VARIANTS = {
  "APRMAY-AUGSEP": ["APRMAY", "MAYJUN", "JUNJUL", "JULAUG", "AUGSEP"],
  "MAYJUN-AUGSEP": ["MAYJUN", "JUNJUL", "JULAUG", "AUGSEP"],
  "APRMAY-JULAUG": ["APRMAY", "MAYJUN", "JUNJUL", "JULAUG"],
  "MAYJUN-JULAUG": ["MAYJUN", "JUNJUL", "JULAUG"],
};

// ── RELEVANCE CLASSES — NOT an eligibility gate (2026-08-24) ─────────
// Stage 1 used to return a binary eligible list and the optimizer refused
// everything else. Measured against the live book, 26 of 61 PRF policies (43%)
// buy at least one interval that gate calls invalid — MARAPR x12, FEBMAR x10,
// OCTNOV x6, SEPOCT x3, NOVDEC x3, JANFEB x1. A prior that declares 43% of what
// an experienced agent already wrote to be agronomically invalid is not a
// finding about the book; it is a claim about the prior that the prior cannot
// support. So the gate is gone and the CLASSIFICATION stays.
//
// ⚠ THE THREE CLASSES DIFFER IN WHAT WE KNOW, NOT IN WHAT WE MEASURED.
//   CORE          the regional prior's own season. Judgement, still not measured.
//   SHOULDER      intervals DIRECTLY ADJACENT to the core block. This is a
//                 POSITIONAL definition — "the boundary of a prior is the part
//                 of a prior least worth trusting" — and it is deliberately not
//                 an agronomic claim about March or September rainfall.
//   OUTSIDE_CORE  everything else. NOT "agronomically irrelevant" — unexamined.
//
// ⚠ AND NOTHING HERE MAY BE PROMOTED BY INSURANCE PERFORMANCE. If admitting
// FEBMAR improves historical net return, that is a fact about rates and the
// index, not evidence that February rain grows hay. A class may only ever be
// changed by forage evidence (docs/prf-risk-relevance-plan.md), never by the
// optimizer liking the answer. `classifyIntervals` therefore takes no
// performance argument at all — it cannot be influenced by one.
const RELEVANCE = { CORE: "CORE", SHOULDER: "SHOULDER", OUTSIDE_CORE: "OUTSIDE_CORE" };

const LAB = Object.keys(PRF_INTERVALS).map(Number).sort((a, b) => a - b).map((c) => PRF_INTERVALS[c].code);
const mean = (a) => a.reduce((s, x) => s + x, 0) / a.length;
const sd = (a) => { const m = mean(a); return Math.sqrt(mean(a.map((x) => (x - m) ** 2))); };

// ── STAGE 1 ────────────────────────────────────────────────────────
function stage1(methodologyId = "REGIONAL_AGRONOMIC_PRIOR", variant = null) {
  const m = STAGE1_METHODOLOGY[methodologyId];
  if (!m) throw new Error(`stage1: unknown methodology ${methodologyId}`);
  if (m.measured && !m.intervals) {
    throw new Error(`stage1: ${m.id} is declared but not implemented — no measured forage response exists yet`);
  }
  const intervals = variant ? PRIOR_VARIANTS[variant] : m.intervals;
  if (!intervals) throw new Error(`stage1: unknown prior variant ${variant}`);
  const cls = classifyIntervals(intervals);
  return {
    methodology: m.id, methodology_version: m.version, measured: m.measured,
    variant: variant || "APRMAY-AUGSEP", basis: m.basis,
    classes: cls,
    core: LAB.filter((l) => cls[l] === RELEVANCE.CORE),
    shoulder: LAB.filter((l) => cls[l] === RELEVANCE.SHOULDER),
    outside_core: LAB.filter((l) => cls[l] === RELEVANCE.OUTSIDE_CORE),
  };
}

// Classify every interval relative to the core block. Adjacency is computed off
// the calendar order in PRF_INTERVALS, so a different core produces a different
// shoulder with no second list to keep in step. NOTE the year does NOT wrap:
// PRF has eleven intervals and no DEC-JAN, so JANFEB and NOVDEC are genuine
// endpoints, not neighbours.
function classifyIntervals(core) {
  const idx = core.map((l) => LAB.indexOf(l)).filter((i) => i >= 0).sort((a, b) => a - b);
  if (!idx.length) throw new Error("classifyIntervals: core is empty");
  const lo = idx[0], hi = idx[idx.length - 1];
  const out = {};
  for (let i = 0; i < LAB.length; i++) {
    out[LAB[i]] = core.includes(LAB[i]) ? RELEVANCE.CORE
      : (i === lo - 1 || i === hi + 1) ? RELEVANCE.SHOULDER
      : RELEVANCE.OUTSIDE_CORE;
  }
  return out;
}

// Which intervals the optimizer is allowed to allocate to. The admission POLICY
// is the caller's, is recorded on the recommendation, and is separate from the
// classification — that separation is the whole point of dropping the gate.
const ADMIT = { CORE: "core", CORE_SHOULDER: "core+shoulder", ALL: "all" };
function admittedIntervals(s1, admit = ADMIT.CORE_SHOULDER) {
  if (admit === ADMIT.CORE) return s1.core.slice();
  if (admit === ADMIT.CORE_SHOULDER) return LAB.filter((l) => s1.classes[l] !== RELEVANCE.OUTSIDE_CORE);
  if (admit === ADMIT.ALL) return LAB.slice();
  throw new Error(`admittedIntervals: unknown admission policy ${admit}`);
}

// What classes a finished allocation actually touches — reported on every
// candidate so "this proposal leans on an interval we cannot defend" is visible
// on the card rather than inferable from the interval names.
function allocationClasses(alloc, s1) {
  const used = Object.keys(alloc).filter((l) => alloc[l] > 0);
  const w = { CORE: 0, SHOULDER: 0, OUTSIDE_CORE: 0 };
  for (const l of used) w[s1.classes[l]] += alloc[l];
  return {
    used_classes: [RELEVANCE.CORE, RELEVANCE.SHOULDER, RELEVANCE.OUTSIDE_CORE].filter((c) => w[c] > 0),
    weight_by_class: w,
    core_only: w.SHOULDER === 0 && w.OUTSIDE_CORE === 0,
  };
}

// ── STAGE 2 · enumeration, RMA-verified only ───────────────────────
function enumerateLegal(admitted, limits) {
  if (!limits) return null;                       // caller MUST fail closed
  for (const l of admitted) if (!limits[l]) return null;
  const n = admitted.length;
  const minU = admitted.map((l) => Math.round(limits[l].min * 10));
  const maxU = admitted.map((l) => Math.round(limits[l].max * 10));
  const out = [], cur = new Array(n).fill(0);
  (function rec(i, left, used) {
    if (i === n) { if (left === 0 && used >= 2) out.push(cur.slice()); return; }
    cur[i] = 0; rec(i + 1, left, used);
    for (let u = minU[i]; u <= Math.min(maxU[i], left); u++) { cur[i] = u; rec(i + 1, left - u, used + 1); }
    cur[i] = 0;
  })(0, 10, 0);
  return out;
}

// ── STAGE 2 · Pareto frontier ──────────────────────────────────────
// Sweep, not a pairwise filter: at ~10^6 strategies an O(n^2) scan is ~10^12
// comparisons and does not finish. Sort by economics descending and keep a
// strategy only if its stability beats everything already kept — which is
// exactly non-domination in two objectives, in O(n log n).
function paretoFrontier(strategies) {
  const sorted = [...strategies].sort((a, b) =>
    b.net_per_dollar - a.net_per_dollar || (a.win_cv ?? Infinity) - (b.win_cv ?? Infinity));
  const out = [];
  let best = Infinity;
  for (const s of sorted) {
    const cv = s.win_cv ?? Infinity;
    if (cv < best - 1e-12) { out.push(s); best = cv; }
  }
  return out;
}

// ── STAGE 2 · the KNEE, deterministic ──────────────────────────────
// THE ALGORITHM, stated so it can be tested rather than trusted:
//
//   1. Normalise both objectives to [0,1] across the frontier — economics by
//      (x - min)/(max - min), stability the same on win_cv.
//      ⚠ NORMALISATION DOES NOT CHANGE WHICH POINT IS CHOSEN, and an earlier
//      version of this comment claimed it did. Scaling one axis by k multiplies
//      the distance NUMERATOR by k for every point identically while the
//      denominator is constant across points, so the argmax cannot move —
//      derived algebraically and then verified on 4,000 random frontiers, where
//      normalised and raw picked the same point every time. Normalisation is
//      kept so the reported DISTANCE is interpretable (a [0,1] geometry rather
//      than a mix of 0.04-scale and 0.3-scale units), not for correctness.
//      The scale-invariance of the pick is a real property and is asserted.
//   2. Take the chord between the two frontier endpoints.
//   3. The knee is the point of MAXIMUM PERPENDICULAR DISTANCE from that chord,
//      on the side toward the utopia corner (high economics, low CV).
//   4. Ties break deterministically: higher economics first, then the
//      lexicographically smaller label. An AI never chooses.
//
// A frontier of one or two points has no interior, so the knee is the highest-
// economics point — stated rather than left to the geometry, which would divide
// by a zero-length chord.
function selectKnee(frontier) {
  if (!frontier.length) return null;
  if (frontier.length <= 2) return frontier[0];
  const es = frontier.map((s) => s.net_per_dollar);
  const cs = frontier.map((s) => s.win_cv);
  const eMin = Math.min(...es), eMax = Math.max(...es);
  const cMin = Math.min(...cs), cMax = Math.max(...cs);
  const eR = eMax - eMin, cR = cMax - cMin;
  // A degenerate axis means one objective does not vary across the frontier;
  // there is no trade-off to find a knee in.
  if (eR <= 0 || cR <= 0) return frontier[0];
  const pts = frontier.map((s) => ({ s, x: (s.net_per_dollar - eMin) / eR, y: (s.win_cv - cMin) / cR }));
  const A = pts[0], B = pts[pts.length - 1];
  const dx = B.x - A.x, dy = B.y - A.y;
  const len = Math.hypot(dx, dy);
  if (len <= 0) return frontier[0];
  let best = null;
  for (const p of pts) {
    // Perpendicular distance from the chord.
    const dist = Math.abs(dy * (p.x - A.x) - dx * (p.y - A.y)) / len;
    if (!best || dist > best.dist + 1e-12) { best = { dist, p }; continue; }
    if (Math.abs(dist - best.dist) <= 1e-12) {
      const a = p.s, b = best.p.s;
      if (a.net_per_dollar > b.net_per_dollar ||
          (a.net_per_dollar === b.net_per_dollar && String(a.label) < String(b.label))) best = { dist, p };
    }
  }
  return best.p.s;
}

// ── STAGE 2 · CONSISTENCY, subject to an economic-retention floor ──
// The most stable strategy that still retains at least `retention` of the
// frontier's maximum economics. The threshold is a POLICY CHOICE, so it is a
// parameter and the sensitivity across candidate thresholds is reported rather
// than one being picked by intuition.
function selectConsistency(frontier, retention) {
  if (!frontier.length) return null;
  const maxNet = Math.max(...frontier.map((s) => s.net_per_dollar));
  // A negative maximum would invert the inequality — retention of a loss is not
  // a floor. Refuse rather than return something plausible.
  if (!(maxNet > 0)) return null;
  const floor = maxNet * retention;
  const eligible = frontier.filter((s) => s.net_per_dollar >= floor - 1e-12);
  if (!eligible.length) return null;
  return eligible.reduce((a, b) => {
    if (b.win_cv < a.win_cv - 1e-12) return b;
    if (Math.abs(b.win_cv - a.win_cv) <= 1e-12) {
      if (b.net_per_dollar > a.net_per_dollar) return b;
      if (b.net_per_dollar === a.net_per_dollar && String(b.label) < String(a.label)) return b;
    }
    return a;
  });
}

// ── ERA ROBUSTNESS ────────────────────────────────────────────────
// Descriptive, per candidate. NOT combined into a score — the point is to show
// whether a candidate's advantage survives being cut into eras, not to rank on it.
function eraRobustness(yearly, producerPerDollar, years, frontierKeys, windowLen = 20) {
  const netAll = mean(yearly) - producerPerDollar;
  const tail = (n) => {
    const sl = yearly.slice(Math.max(0, yearly.length - n));
    return sl.length ? mean(sl) - producerPerDollar : null;
  };
  const wins = [];
  for (let s = 0; s + windowLen <= yearly.length; s++) wins.push(mean(yearly.slice(s, s + windowLen)) - producerPerDollar);
  const sorted = [...wins].sort((a, b) => a - b);
  const q = (p) => (sorted.length ? sorted[Math.min(sorted.length - 1, Math.round(p * (sorted.length - 1)))] : null);
  let run = 0, dry = 0;
  for (const v of yearly) { run = v > 0 ? 0 : run + 1; if (run > dry) dry = run; }
  const nets = yearly.map((v) => v - producerPerDollar).sort((a, b) => a - b);
  return {
    full_record: netAll,
    last_30: tail(30), last_20: tail(20),
    rolling: wins.length ? {
      n: wins.length, window: windowLen, mean: mean(wins), sd: sd(wins),
      min: sorted[0], p25: q(0.25), median: q(0.5), p75: q(0.75), max: sorted[sorted.length - 1],
      share_positive: wins.filter((w) => w > 0).length / wins.length,
    } : null,
    // Share of rolling windows in which this strategy was itself Pareto-efficient
    // (or within `nearPct` of the window's best economics at no worse stability).
    share_windows_efficient: frontierKeys == null ? null : frontierKeys,
    payout_freq: yearly.filter((v) => v > 0).length / yearly.length,
    longest_dry_spell: dry,
    worst_year: nets[0],
    p10_net: nets[Math.floor(0.1 * (nets.length - 1))],
  };
}

// ── CANDIDATE IDENTITY ────────────────────────────────────────────
// ⚠ THE INTERNAL ID AND THE UI LABEL ARE DIFFERENT STRINGS ON PURPOSE.
// PARETO_KNEE is a geometric fact about a frontier. Calling it "the Silas
// recommendation" would assert that the knee is the right producer choice, and
// that has NOT been validated across grids. The UI says "Balanced Tradeoff",
// which describes what it is rather than what anyone should do.
// ⚠ HISTORICAL_ECONOMICS_MAXIMUM IS A BOUNDARY, NOT A PRODUCER OPTION, and the
// book is what demoted it. Measured over all 61 eligible PRF policies: it is a
// TWO-INTERVAL allocation in 61 of 61 cases and puts >= 70% of value in a single
// interval in 55 of 61 (90%) — always against the county cap. That is the
// objective working exactly as written, and the answer is a corner solution.
// Offering the mathematical maximum of historical payouts beside two real
// structures implies it is a comparable choice. It is the edge of the frontier,
// it is worth SEEING, and `analytical_boundary` is what tells a surface to
// render it as one.
const CANDIDATE = {
  HISTORICAL_ECONOMICS_MAXIMUM: { id: "HISTORICAL_ECONOMICS_MAXIMUM", ui: "Historical Economics Maximum",
    analytical_boundary: true,
    note: "The upper bound of historical net return per dollar on this frontier — in practice a two-interval position against the county cap. Shown as the edge of what was possible, not as a recommended structure." },
  // ⚠ RESEARCH ONLY — NOT PRODUCER-FACING. Rejected 2026-08-24 on the book
  // evidence above. Kept so the two objectives can be compared, and so a future
  // reader can see what it does rather than take this note on trust.
  PARETO_KNEE: { id: "PARETO_KNEE", ui: "Balanced Tradeoff", analytical_boundary: false, research_only: true,
    note: "The knee of the economics-vs-stability frontier — maximum perpendicular distance from the chord between its endpoints. Deterministic geometry. NOT validated as the right producer choice." },
  // ⚠ THE NAME CARRIES THE CAVEAT. Severe years are defined from the RAINFALL
  // INDEX, not from observed forage loss — we cannot measure forage response at
  // all today (docs/prf-risk-relevance-plan.md, Step 0). Calling this
  // "drought-conditioned" would claim an agronomic observation nobody made.
  INDEX_SEVERITY_CONDITIONED: { id: "INDEX_SEVERITY_CONDITIONED", ui: "Index-Severity Weighted",
    analytical_boundary: false, research_only: false,
    note: "The allocation giving the most average net in the years this grid's rainfall INDEX was lowest across the core season, subject to retaining a stated share of long-run economics and never covering fewer low-index years than the current contract. Severe years are low-INDEX years, not measured forage-loss years. NOT validated as the right producer choice." },
  CONSISTENCY: { id: "CONSISTENCY", ui: "Consistency", analytical_boundary: false, research_only: false,
    note: "The most stable strategy that still retains most of the maximum historical economics." },
};

// ── COVERAGE LEVEL — analysed SEPARATELY, never optimized (2026-08-24) ─
// The V1 optimizer holds coverage at what the producer already carries and
// varies only the interval allocation. It used to search coverage too, and the
// result was degenerate: across all 61 policies, EVERY candidate at EVERY grid
// landed on 90% — 61 of 61 — because at CY2026 rates 90% dominates every lower
// level on both objectives at once. 48 of those policies carry 85% today, so
// the tool would have told four growers in five to move up, always, for a
// reason that is about the subsidy and rate structure rather than about their
// farm.
//
// A constant that never loses is not an optimization result, and presenting it
// as one launders an actuarial artefact into a recommendation. So the ladder is
// its own comparison: ONE allocation, priced at every coverage level, with the
// tradeoff shown and no level marked as the answer.
//
// ⚠ It returns `dominates_all_lower` per level rather than hiding it. If 90%
// really is better on both axes for this grid, the agent should be able to see
// that stated — and see that it is a property of the rate table, not of the
// allocation they just chose.
function coverageLadder(alloc, priceAt, simulateAt, levels = [0.70, 0.75, 0.80, 0.85, 0.90]) {
  const rows = [];
  for (const cov of levels) {
    const priced = priceAt(alloc, cov);
    if (!priced) { rows.push({ coverage: cov, available: false }); continue; }
    const sim = simulateAt(alloc, cov);
    rows.push({
      coverage: cov, available: true,
      protection: priced.total_protection, producer_premium: priced.producer_premium,
      avg_indemnity: sim.avg_indemnity, avg_net: sim.avg_indemnity - priced.producer_premium,
      net_per_dollar: priced.total_protection > 0
        ? (sim.avg_indemnity - priced.producer_premium) / priced.total_protection : null,
      win_cv: sim.win_cv ?? null, payout_freq: sim.payout_freq ?? null,
    });
  }
  const live = rows.filter((r) => r.available && r.net_per_dollar != null);
  for (const r of live) {
    const lower = live.filter((o) => o.coverage < r.coverage);
    r.dominates_all_lower = lower.length > 0 && lower.every((o) =>
      r.net_per_dollar >= o.net_per_dollar - 1e-12 && (r.win_cv ?? Infinity) <= (o.win_cv ?? Infinity) + 1e-12);
  }
  return rows;
}

// ── SEVERITY — how bad was the forage year, independent of the bet ──
// PRF is bought for drought. A structure that wins on a 77-year average while
// being worse in the driest years is the wrong product, and an unconditional
// average cannot tell you which one you have.
//
// ⚠ SEVERITY IS MEASURED ON THE CORE FORAGE SEASON, NOT ON THE PAYOUT.
// Ranking years by what a strategy paid would be circular — every strategy
// would define a bad year as one in which it happened to pay, and every
// strategy would then look good in bad years. The classifier therefore takes
// the INDEX SERIES and the CORE intervals, and never a strategy, an allocation
// or a return. It is the same discipline as classifyIntervals: the function
// cannot be influenced by performance because it cannot see performance.
//
// The core-season index for a year is the MEAN index across the core intervals,
// which is RMA's own measure of rainfall against that grid's normal. Years are
// then ranked within the grid's OWN history, so "severe" means severe for this
// grid rather than severe against some regional constant.
const SEVERITY = { SEVERE: "SEVERE", POOR: "POOR", NORMAL: "NORMAL" };

function severityByYear(series, years, core, breaks = [0.20, 0.40]) {
  const [lo, hi] = breaks;
  if (!(lo > 0 && lo < hi && hi < 1)) throw new Error("severityByYear: breaks must satisfy 0 < lo < hi < 1");
  const usable = core.filter((l) => series[l]);
  if (!usable.length) throw new Error("severityByYear: no core interval has an index series");
  const rows = years.map((y) => {
    const vals = usable.map((l) => series[l][y]).filter((v) => Number.isFinite(v));
    return { year: y, core_index: vals.length ? vals.reduce((a, b) => a + b, 0) / vals.length : null };
  }).filter((r) => r.core_index != null);
  const sorted = [...rows].sort((a, b) => a.core_index - b.core_index);
  // Percentile by rank within this grid's own record, with tied years taking the
  // AVERAGE of the ranks they span.
  //
  // ⚠ TIES CANNOT TAKE THE LOWEST RANK, and the degenerate case is what proves
  // it: a grid whose core index never varies would then have EVERY year at rank
  // 0 and every year classified SEVERE — a record with no drought in it read as
  // nothing but drought. Averaging sends that case to the middle, where it
  // belongs: if no year is drier than any other, no year is a drought. It also
  // keeps a tied pair straddling a class boundary together, which taking either
  // extreme would do as well but only by luck of the sort order.
  const rankOf = new Map();
  for (let i = 0; i < sorted.length; ) {
    const v = sorted[i].core_index;
    let j = i;
    while (j < sorted.length && sorted[j].core_index === v) j++;
    rankOf.set(v, (i + j - 1) / 2);
    i = j;
  }
  const n = sorted.length;
  for (const r of rows) {
    r.pctile = n > 1 ? rankOf.get(r.core_index) / (n - 1) : 0;
    r.klass = r.pctile <= lo ? SEVERITY.SEVERE : r.pctile <= hi ? SEVERITY.POOR : SEVERITY.NORMAL;
  }
  return { breaks, n: rows.length, core_used: usable, rows,
           counts: { SEVERE: rows.filter((r) => r.klass === "SEVERE").length,
                     POOR: rows.filter((r) => r.klass === "POOR").length,
                     NORMAL: rows.filter((r) => r.klass === "NORMAL").length } };
}

// The head-to-head, cut by severity class. Same signed-incremental convention
// as headToHead — every figure is proposed MINUS current.
//
// ⚠ `years` on the strategies must be the SAME ordered year list the severity
// classes were built from, because the payout arrays are positional. The
// caller passes it explicitly rather than letting two orderings drift.
function headToHeadBySeverity(proposed, current, sev, years) {
  if (!proposed || !current || !sev) return null;
  const idxOf = new Map(years.map((y, i) => [y, i]));
  const out = {};
  for (const klass of [SEVERITY.SEVERE, SEVERITY.POOR, SEVERITY.NORMAL]) {
    const yrs = sev.rows.filter((r) => r.klass === klass).map((r) => r.year).filter((y) => idxOf.has(y));
    if (!yrs.length) { out[klass] = null; continue; }
    let pSum = 0, cSum = 0, pW = 0, cW = 0, tie = 0, pPaid = 0, cPaid = 0;
    let pWorst = Infinity, cWorst = Infinity;
    for (const y of yrs) {
      const i = idxOf.get(y);
      const pNet = proposed.yearly[i] * proposed.protection - proposed.producer_premium;
      const cNet = current.yearly[i] * current.protection - current.producer_premium;
      pSum += pNet; cSum += cNet;
      if (pNet > cNet + 1e-9) pW++; else if (cNet > pNet + 1e-9) cW++; else tie++;
      if (proposed.yearly[i] > 0) pPaid++;
      if (current.yearly[i] > 0) cPaid++;
      if (pNet < pWorst) pWorst = pNet;
      if (cNet < cWorst) cWorst = cNet;
    }
    const n = yrs.length;
    out[klass] = {
      years: n,
      proposed_avg_net: pSum / n, current_avg_net: cSum / n, additional_avg_net: (pSum - cSum) / n,
      years_proposed_better: pW, years_current_better: cW, years_tied: tie,
      proposed_paid: pPaid, current_paid: cPaid,
      proposed_worst_net: pWorst, current_worst_net: cWorst,
      additional_worst_net: pWorst - cWorst,
    };
  }
  return out;
}

// ── ALTERNATIVE SEVERITY DEFINITIONS — a falsification harness ──────
// The live candidate defines severe years as the driest fifth by MEAN CORE
// INDEX, and then optimises allocation over those same core intervals. That is
// a circularity risk, not a proven defect: an interval that is volatile enough
// to drag the core mean down helps DEFINE the years it is then rewarded for
// covering. MAYJUN takes the largest allocation in 24 of 29 candidates, so the
// question is live.
//
// ⚠ THESE DEFINITIONS ARE NOT COMBINED AND NEVER WILL BE. A composite would
// launder six disagreeing views into one confident number. They are run
// separately and the question asked of them is AGREEMENT — how often the same
// intervals survive — which is a falsification test, not a score.
//
// ⚠ AND NONE OF THEM OBSERVE FORAGE. Every one is a statement about RAINFALL
// INDEX, so a "severe year" here is a year the index was low. It is NOT a
// measured forage-loss year and must never be described as one — see
// docs/prf-risk-relevance-plan.md, where Step 0 established we cannot measure
// forage response at all today.
const SEVERITY_DEFS = {
  // The shipped definition, and the three quantile variants of it.
  MEAN_CORE_P20: { id: "MEAN_CORE_P20", label: "driest 20% by mean core index",
    fn: (series, years, core) => severityByYear(series, years, core, [0.20, 0.40]) },
  MEAN_CORE_P10: { id: "MEAN_CORE_P10", label: "driest 10% by mean core index",
    fn: (series, years, core) => severityByYear(series, years, core, [0.10, 0.30]) },
  MEAN_CORE_P25: { id: "MEAN_CORE_P25", label: "driest 25% by mean core index",
    fn: (series, years, core) => severityByYear(series, years, core, [0.25, 0.45]) },
  // Count-based: a year is severe when SEVERAL core intervals were individually
  // poor. This does not average, so one catastrophic interval cannot carry a
  // year on its own — a structurally different question from the quantiles.
  MULTI_BELOW_80: { id: "MULTI_BELOW_80", label: "2+ core intervals below 80% of normal",
    fn: (series, years, core) => countBelowSeverity(series, years, core, 0.80, 2) },
  MULTI_BELOW_85: { id: "MULTI_BELOW_85", label: "2+ core intervals below 85% of normal",
    fn: (series, years, core) => countBelowSeverity(series, years, core, 0.85, 2) },
  // Consecutive: the worst back-to-back pair of core intervals. Forage stress
  // compounds through a season, and a run of two dry intervals is a different
  // event from two dry intervals at opposite ends of it.
  WORST_CONSECUTIVE: { id: "WORST_CONSECUTIVE", label: "worst consecutive core interval pair",
    fn: (series, years, core) => consecutiveSeverity(series, years, core, [0.20, 0.40]) },
};

// A year is severe when at least `minCount` core intervals sat below
// `threshold` of normal. The index IS percent-of-normal, so 0.80 is literal.
// POOR is the same test at one fewer interval; everything else is NORMAL.
function countBelowSeverity(series, years, core, threshold, minCount) {
  const usable = core.filter((l) => series[l]);
  if (!usable.length) throw new Error("countBelowSeverity: no core interval has an index series");
  const rows = [];
  for (const y of years) {
    const vals = usable.map((l) => series[l][y]).filter((v) => Number.isFinite(v));
    if (!vals.length) continue;
    const below = vals.filter((v) => v < threshold).length;
    rows.push({ year: y, core_index: vals.reduce((a, b) => a + b, 0) / vals.length, below,
      klass: below >= minCount ? SEVERITY.SEVERE : below >= minCount - 1 ? SEVERITY.POOR : SEVERITY.NORMAL });
  }
  return {
    breaks: [threshold, minCount], n: rows.length, core_used: usable, rows,
    counts: { SEVERE: rows.filter((r) => r.klass === "SEVERE").length,
              POOR: rows.filter((r) => r.klass === "POOR").length,
              NORMAL: rows.filter((r) => r.klass === "NORMAL").length },
  };
}

// Rank years by their WORST adjacent pair of core intervals rather than by the
// season mean. Adjacency is calendar order within the core block and does not
// wrap — the same rule classifyIntervals follows.
function consecutiveSeverity(series, years, core, breaks = [0.20, 0.40]) {
  const usable = core.filter((l) => series[l]).sort((a, b) => LAB.indexOf(a) - LAB.indexOf(b));
  if (usable.length < 2) throw new Error("consecutiveSeverity: needs at least two core intervals with a series");
  const worstPair = {};
  for (const y of years) {
    let worst = Infinity;
    for (let i = 0; i + 1 < usable.length; i++) {
      const a = series[usable[i]][y], b = series[usable[i + 1]][y];
      if (!Number.isFinite(a) || !Number.isFinite(b)) continue;
      const m = (a + b) / 2;
      if (m < worst) worst = m;
    }
    if (Number.isFinite(worst)) worstPair[y] = worst;
  }
  const ys = years.filter((y) => worstPair[y] != null);
  // Reuse the ranking machinery by handing it a synthetic one-interval series.
  const synth = { __PAIR__: worstPair };
  const out = severityByYear(synth, ys, ["__PAIR__"], breaks);
  out.core_used = usable;
  return out;
}

// ⚠ LEAVE-ONE-INTERVAL-OUT. When asking how much an allocation to X is worth,
// define the severe years WITHOUT X. Otherwise a volatile interval helps pick
// the years it is then measured against, and the answer flatters it by
// construction. Returns null when removing X leaves nothing to rank on.
function severityExcluding(series, years, core, exclude, def = SEVERITY_DEFS.MEAN_CORE_P20) {
  const rest = core.filter((l) => l !== exclude);
  if (rest.length < 2) return null;
  try { return def.fn(series, years, rest); } catch (_) { return null; }
}

// Cap the per-interval maximum below what RMA allows, for concentration
// sensitivity. RMA's minimum is untouched — a cap that fell below the minimum
// would make the allocation illegal rather than diversified.
function cappedLimits(limits, cap) {
  if (!limits) return null;
  const out = {};
  for (const l of Object.keys(limits)) {
    const min = limits[l].min, max = Math.min(limits[l].max, cap);
    if (max < min) return null;
    out[l] = { min, max };
  }
  return out;
}

// ── THE SEVERITY-CONDITIONED CANDIDATE ─────────────────────────────
// PARETO_KNEE was rejected as producer-facing on 2026-08-24, on evidence rather
// than taste. Over the 61-policy book at fixed coverage it increased the number
// of severe seasons that PAY in **0 of 45** comparable policies, improved the
// worst single year in 33%, and in 27% it improved normal-year economics while
// REDUCING severe-year performance. It optimises economics against steadiness,
// and neither axis is drought.
//
// This candidate optimises the thing the product is bought for: what arrives
// when the forage season fails. The frontier axes are SEVERE-YEAR NET and
// FULL-RECORD NET — protection versus long-run cost — and rolling stability
// drops out of the objective entirely (it is still reported).
//
// ⚠ IT MAY RETURN NOTHING. `NO_MATERIAL_IMPROVEMENT` is a first-class answer.
// A tool that always proposes a change is a tool that proposes changes for its
// own sake, and 16% of the book was already made WORSE on average by the
// previous candidate. Current coverage stands unless a proposal clears a stated
// bar in severe years.

// The nine measures, per allocation, under one severity classification.
// `yearly` is the per-year payment FACTOR (fraction of protection).
function severityMetrics(yearly, sev, years, protection, producerPremium) {
  if (!sev || !yearly || !years) return null;
  const idxOf = new Map(years.map((y, i) => [y, i]));
  const out = {};
  for (const klass of [SEVERITY.SEVERE, SEVERITY.POOR, SEVERITY.NORMAL]) {
    const yrs = sev.rows.filter((r) => r.klass === klass).map((r) => r.year).filter((y) => idxOf.has(y));
    if (!yrs.length) { out[klass] = null; continue; }
    let indem = 0, net = 0, paid = 0, paidSum = 0, worst = Infinity;
    for (const y of yrs) {
      const i = idxOf.get(y);
      const ind = yearly[i] * protection;
      const n = ind - producerPremium;
      indem += ind; net += n;
      if (yearly[i] > 0) { paid++; paidSum += ind; }
      if (n < worst) worst = n;
    }
    const n = yrs.length;
    out[klass] = {
      years: n,
      avg_indemnity: indem / n,
      avg_net: net / n,
      // ⚠ FREQUENCY AND MAGNITUDE ARE SEPARATE QUESTIONS. A strategy can raise
      // the average by paying more in the few severe years it already covered
      // while covering no more of them — which is exactly what the rejected
      // candidate did across the whole book.
      pay_freq: paid / n,
      avg_payment_when_paid: paid ? paidSum / paid : null,
      worst_net: worst,
    };
  }
  return out;
}

// Pareto on (severe-year net, full-record net). Both maximised, both per dollar
// of protection so the shape is comparable across policies. Same O(n log n)
// sweep as paretoFrontier — sort by one objective, keep a point only if it beats
// everything kept on the other.
function severityFrontier(strategies) {
  const usable = strategies.filter((s) => Number.isFinite(s.severe_net_per_dollar) && Number.isFinite(s.net_per_dollar));
  const sorted = [...usable].sort((a, b) =>
    b.severe_net_per_dollar - a.severe_net_per_dollar ||
    b.net_per_dollar - a.net_per_dollar ||
    String(a.label).localeCompare(String(b.label)));
  const out = [];
  let best = -Infinity;
  for (const s of sorted) {
    if (s.net_per_dollar > best + 1e-12) { out.push(s); best = s.net_per_dollar; }
  }
  return out;
}

// ⚠ THE MATERIALITY BAR IS A POLICY CHOICE AND IS NOT SETTLED. It is a
// parameter, it is reported with every result, and the caller must state it.
// Defaults exist so the tool runs, not so the question is answered.
const MATERIALITY = {
  // Severe-year net must improve by at least this fraction of what the producer
  // pays in premium today. A gain smaller than that is inside the noise of one
  // interval election and is not worth moving a customer's contract for.
  severeNetGainVsPremium: 0.10,
  // And the proposal may not cover FEWER of the failed seasons than the
  // customer's current allocation already does. This is the measure the
  // rejected candidate never moved; it is a floor, not a target — requiring an
  // INCREASE would reject strategies that pay far more in the same years.
  allowPayFreqDrop: false,
};

// Pick the drought candidate at a given economic-retention floor.
// Returns null (meaning NO_MATERIAL_IMPROVEMENT) when nothing clears the bar.
function selectDroughtCandidate(frontier, current, retention, mat = MATERIALITY) {
  if (!frontier || !frontier.length || !current) return null;
  const maxEcon = Math.max(...frontier.map((s) => s.net_per_dollar));
  // A negative long-run maximum inverts the retention inequality — retaining a
  // fraction of a loss is not a floor. Refuse rather than return something.
  if (!(maxEcon > 0)) return null;
  const floor = maxEcon * retention;
  const bar = mat.severeNetGainVsPremium * (current.producer_premium || 0);

  const eligible = frontier.filter((s) => {
    if (!(s.net_per_dollar >= floor - 1e-12)) return false;
    if (!(s.severe_avg_net - current.severe_avg_net > bar - 1e-9)) return false;
    if (!mat.allowPayFreqDrop && s.severe_pay_freq < current.severe_pay_freq - 1e-9) return false;
    return true;
  });
  if (!eligible.length) return null;

  // Among everything that clears the bar, take the most protection in severe
  // years; ties to long-run economics, then to the label so the pick is stable.
  return eligible.reduce((a, b) => {
    if (b.severe_avg_net > a.severe_avg_net + 1e-9) return b;
    if (Math.abs(b.severe_avg_net - a.severe_avg_net) <= 1e-9) {
      if (b.net_per_dollar > a.net_per_dollar + 1e-12) return b;
      if (Math.abs(b.net_per_dollar - a.net_per_dollar) <= 1e-12 && String(b.label) < String(a.label)) return b;
    }
    return a;
  });
}

// ── HEAD TO HEAD against the coverage the customer actually has ────
// Every figure is INCREMENTAL and signed against current. The year counts are
// what stop a favourable average from standing on its own: a proposal can beat
// current on the mean and lose in most individual years.
//
// ⚠ "outperformed" compares NET in a year, not indemnity. Premium is paid every
// year whether it pays or not, so comparing indemnity alone flatters whichever
// option costs more.
function headToHead(proposed, current) {
  if (!proposed || !current) return null;
  const n = Math.min(proposed.yearly.length, current.yearly.length);
  let pWins = 0, cWins = 0, ties = 0, bothPaid = 0, onlyP = 0, onlyC = 0, neither = 0;
  for (let i = 0; i < n; i++) {
    const pNet = proposed.yearly[i] * proposed.protection - proposed.producer_premium;
    const cNet = current.yearly[i] * current.protection - current.producer_premium;
    if (pNet > cNet + 1e-9) pWins++; else if (cNet > pNet + 1e-9) cWins++; else ties++;
    const pPaid = proposed.yearly[i] > 0, cPaid = current.yearly[i] > 0;
    if (pPaid && cPaid) bothPaid++;
    else if (pPaid) onlyP++;
    else if (cPaid) onlyC++;
    else neither++;
  }
  const avgIndemP = (proposed.avg_factor ?? 0) * proposed.protection;
  const avgIndemC = (current.avg_factor ?? 0) * current.protection;
  return {
    years: n,
    additional_protection: proposed.protection - current.protection,
    additional_producer_premium: proposed.producer_premium - current.producer_premium,
    additional_avg_indemnity: avgIndemP - avgIndemC,
    additional_avg_net: (avgIndemP - proposed.producer_premium) - (avgIndemC - current.producer_premium),
    years_proposed_better: pWins,
    years_current_better: cWins,
    years_tied: ties,
    years_both_paid: bothPaid,
    years_only_proposed_paid: onlyP,
    years_only_current_paid: onlyC,
    years_neither_paid: neither,
  };
}

module.exports = {
  STAGE1_METHODOLOGY, PRIOR_VARIANTS, LAB, CANDIDATE, RELEVANCE, ADMIT, SEVERITY,
  severityByYear, headToHeadBySeverity,
  MATERIALITY, severityMetrics, severityFrontier, selectDroughtCandidate,
  SEVERITY_DEFS, countBelowSeverity, consecutiveSeverity, severityExcluding, cappedLimits,
  stage1, classifyIntervals, admittedIntervals, allocationClasses,
  enumerateLegal, paretoFrontier, selectKnee, selectConsistency, eraRobustness,
  coverageLadder, headToHead,
};
